import React, { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Row, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import packageJson from '../../../../../../package.json';
import { IFormSignupComponent } from "../../../business/core";
import { FormSignupControllerImpl } from "../../../business/impl";

export const FormSignupComponent: React.FC<IFormSignupComponent> = ({ setStep, step, setData, formData }) => {
    const navigate = useNavigate();
    const version = packageJson.version;
    const { t } = useTranslation('account');
    const [validated, setValidated] = useState(false);
    const [strengthPasswordLevel, setStrengthPasswordLevel] = useState<string>('Moderate');
    const [name, setName] = useState<string>(() => {
        const value = formData?.get("name");
        return typeof value === 'string' ? value : '';
    });
    const [nameError, setNameError] = useState(false);
    const [phone, setPhone] = useState<string>(() => {
        const value = formData?.get("phone");
        return typeof value === 'string' ? value : '';
    });
    const [phoneError, setPhoneError] = useState(false);
    const [email, setEmail] = useState<string>(() => {
        const value = formData?.get("email");
        return typeof value === 'string' ? value : '';
    });
    const [emailError, setEmailError] = useState(false);
    const [password, setPassword] = useState<string>('');
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordConfirmError, setPasswordConfirmError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [toastingText, setToastingText] = useState<string | null>(null);
    const [termsChecked, setTermsChecked] = useState(formData ? formData.get("terms") : false);
    const [ageChecked, setAgeChecked] = useState(formData ? formData.get("age") : false);

    const [showPassword, setShowPassword] = useState(false);

    const controller = new FormSignupControllerImpl();

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await controller.onLoad();
            setStrengthPasswordLevel(response);
        } catch (error) {
            console.error("Erro na requisição:", error);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        fetchData();
    }, [strengthPasswordLevel]);

    const handleSubmit = async (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();

        setValidated(true);
        setNameError(false);
        setPhoneError(false);
        setEmailError(false);
        setPasswordError(false);
        setPasswordConfirmError(false);

        const submitedFormData = new FormData();

        if (!name || name === "") {
            setValidated(false);
            setNameError(true);
            setIsLoading(false);
            return false;
        }
        submitedFormData.append("name", name);

        if (!phone || phone === "") {
            setValidated(false);
            setPhoneError(true);
            setIsLoading(false);
            return false;
        }

        const numericPhone = phone.replace(/\D/g, "");

        if (numericPhone.length !== 11) {
            setValidated(false);
            setPhoneError(true);
            return;
        }

        if (/^(\d)\1+$/.test(numericPhone)) {
            setValidated(false);
            setPhoneError(true);
            return;
        }

        if (numericPhone[2] !== "9") {
            setValidated(false);
            setPhoneError(true);
            window.scrollTo(0, 0);
            return;
        }
        submitedFormData.append("phone", phone);

        if (!email || email === "") {
            setValidated(false);
            setEmailError(true);
            setIsLoading(false);
            return false;
        }
        submitedFormData.append("email", email);

        if (!password || password === "") {
            setValidated(false);
            setPasswordError(true);
            setIsLoading(false);
            return false;
        }
        if (passwordConfirm !== password) {
            setValidated(false);
            setPasswordConfirmError(true);
            setIsLoading(false);
            setToastingText(t('passwordConfirm'));
            return false;
        }
        submitedFormData.append("password", btoa(password));

        if (!termsChecked) {
            setIsLoading(false);
            return false;
        }
        submitedFormData.append("terms", `${termsChecked}`);

        if (!ageChecked) {
            setIsLoading(false);
            return false;
        }
        submitedFormData.append("age", `${ageChecked}`);
        if (!setData) return false;

        if (form.checkValidity() === false) {
        } else {
            setIsLoading(true);
            try {
                const response = await controller.signup(submitedFormData);
                if (response === 'success') {
                    setData(submitedFormData);
                    setStep(2)
                    return true;
                }
                setValidated(false)
                switch (response) {
                    case "name":
                        setNameError(true);
                        break;
                    case "phone":
                        setPhoneError(true);
                        break;
                    case "email":
                        setEmailError(true);
                        break;
                    case "password":
                        setPasswordError(true);
                        break;
                    default:
                        setToastingText(t(response))
                        break
                }
            } catch (error) {
                setToastingText(t(error.message));
            }
            window.scrollTo(0, 0);
            setIsLoading(false);
        }
    };

    return (
        <>
            <Form className="mt-4" onSubmit={handleSubmit} validated={validated}>
                <Form.Group className="mb-3" controlId="formBasicName">
                    <Form.Control
                        required
                        placeholder={t('placeholder_name')}
                        onChange={(e) => setName(e.target.value)}
                        className="custom-input text-capitalize"
                        disabled={isLoading}
                        isInvalid={nameError}
                        autoFocus
                        autoComplete="off"
                        value={name}
                    />
                    <Form.Control.Feedback type="invalid">
                        {nameError ? t('feedback_name') : ""}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicPhone">
                    <InputMask
                        mask={t('inputMask')}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={isLoading}
                    >
                        {(inputProps) => (
                            <Form.Control
                                {...inputProps}
                                type="tel"
                                required
                                minLength={20}
                                className={`custom-input ${phoneError ? "input-error" : ""}`}
                                placeholder={t('placeholder_phone')}
                                isInvalid={phoneError}
                                autoComplete="off"
                                disabled={isLoading}
                            />
                        )}
                    </InputMask>
                    <Form.Control.Feedback type="invalid">
                        {t('feedback_phone')}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicEmail">
                    <Form.Control
                        type="email"
                        required
                        placeholder={t('placeholder_email')}
                        onChange={(e) => setEmail(e.target.value)}
                        className="custom-input"
                        isInvalid={emailError}
                        disabled={isLoading}
                        autoComplete="off"
                        value={email}
                    />
                    <Form.Control.Feedback type="invalid">
                        {t('feedback_email')}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicPassword">
                    <InputGroup hasValidation>
                        <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            required
                            minLength={6}
                            placeholder={t('placeholder_password')}
                            onChange={(e) => setPassword(e.target.value)}
                            className="custom-input"
                            isInvalid={passwordError}
                            disabled={isLoading}
                            value={password}
                            aria-describedby="passwordFeedback"
                        />
                        <Button variant="outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                        <Form.Control.Feedback id="passwordFeedback" type="invalid">
                            {t('feedback_password')} {t(`password_strength_string.${strengthPasswordLevel}`)}
                        </Form.Control.Feedback>
                    </InputGroup>
                    <Form.Text id="passwordHelpBlock" muted>
                        <p dangerouslySetInnerHTML={{ __html: t(`password_strength_html.${strengthPasswordLevel}`) }} />
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicPasswordConfirm">
                    <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={6}
                        placeholder={t('placeholder_passwordConfirm')}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        className="custom-input"
                        isInvalid={passwordConfirmError}
                        disabled={isLoading}
                        value={passwordConfirm}
                        aria-describedby="passwordConfirmFeedback"
                    />
                    <Form.Control.Feedback id="passwordConfirmFeedback" type="invalid">
                        {passwordConfirmError ? t('feedback_password_mismatch') : t('feedback_password_confirm')}
                    </Form.Control.Feedback>
                </Form.Group>
                <fieldset disabled={isLoading}>
                    <Form.Group className="mt-3 mb-3">
                        <Form.Check
                            required
                            label={(
                                <>
                                    {t('agree')}
                                    <a href="/sua-url">{t('terms')}</a>
                                </>
                            )}
                            feedback={t('feedback')}
                            feedbackType="invalid"
                            onChange={(e) => setTermsChecked(e.target.checked)}
                            checked={termsChecked === true ? true : false}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check
                            required
                            label={(
                                <>
                                    {t('ageToUseCheckbox')}
                                </>
                            )}
                            feedback={t('feedback_age')}
                            feedbackType="invalid"
                            onChange={(e) => setAgeChecked(e.target.checked)}
                            checked={ageChecked === true ? true : false}
                        />
                    </Form.Group>
                    <div className="row">
                        <div className="col">
                            <Button
                                variant="primary"
                                type="button"
                                className="px-4 button-flat"
                                onClick={() => { navigate('/auth', { state: { reload: true } }); }}
                                disabled={isLoading}
                            >
                                {!isLoading ? (
                                    t('back_button')
                                ) : (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">...</span>
                                    </Spinner>
                                )}
                            </Button>
                        </div>
                        <div className="col d-flex flex-row-reverse">
                            <Button
                                variant="primary"
                                type="submit"
                                className="px-4 button-flat"
                                disabled={isLoading}
                            >
                                {!isLoading ? (
                                    t('signup_button')
                                ) : (
                                    <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">Loading</span>
                                    </Spinner>
                                )}
                            </Button>
                        </div>
                    </div>
                    <Row>
                        <Col className="text-center mt-2"><small style={{ color: '#999999' }}>v{version}</small></Col>
                    </Row>
                </fieldset>
            </Form>
            <ToastContainer position="top-end">
                <Toast
                    bg={"danger"}
                    onClose={() => setToastingText(null)}
                    show={toastingText !== null ? true : false}
                    delay={5000}
                    autohide
                >
                    <Toast.Body className={"text-white"}>
                        {toastingText !== null ? toastingText : null}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};
